import Vue from "vue";
import api from "@/api";

import deepClone from "@/utils/deep-clone";

const state = {
    list: [],
    details: {},
    listByUser: {},
};

const getters = {
    list(state) {
        return deepClone(state.list);
    },
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    listByUser(state) {
        return deepClone(state.listByUser);
    },
};

const actions = {
    getList({ commit, rootGetters }, { payload: { user, filter } } = { payload: {} }) {
        let query = "all";

        if (user) {
            query = user;
        } else if (filter) {
            if (filter === "all") {
                query = "all";
            } else if (filter === "my") {
                query = rootGetters["users/current/user"]._id;
            }
        }

        return api.get(`/api/feedback/get/user/${query}/sets/all`).then(res => {
            const list = res.data;

            if (Array.isArray(list)) {
                commit("updateList", { list });
            } else {
                commit("updateList", { list: [] });
            }
        });
    },
    getDetails({ commit, rootGetters }, { payload: { id } }) {
        const user = rootGetters["users/current/user"];

        if (!user) {
            return;
        }

        return api.get(`/api/feedback/get/user/${user._id}/sets/details/${id}`).then(res => {
            const feedback = res.data;

            if (feedback) {
                commit("updateDetails", { feedback });
            }
        });
    },

    create(
        { dispatch },
        {
            payload: {
                type,
                visibility,
                customQuestion = null,
                signals,
                receiver = null,
                read = false,
                usersArray = [],
                teamsArray = [],
            },
        },
    ) {
        const data = {
            feedbackSignals: signals,
            receiver,
            multiReceiver: usersArray, // only needed for praises
            multiTeamReceiver: teamsArray, // only needed for praises
            type,
            visibility,
            read,
            customQuestion,
            updateExisting: true, // needed to distinguish from Gmail / Slack requests where single signals are simply attached to set / newly created
        };

        return api.post(`/api/feedback/create`, data).then(() => {
            return dispatch("getList");
        });
    },

    remove(_, { payload: { id } }) {
        return api.post(`/api/feedback/remove`, { _id: id });
    },

    read(_, { payload: { id } }) {
        return api.post(`/api/feedback/update/read`, {
            read: true,
            feedbackSet: id,
        });
    },

    like(_, { payload: { id, direction } }) {
        return api.post(`/api/feedback/update/likes`, {
            feedbackSet: id,
            direction, // 'add' || 'remove'
        });
    },

    createComment(_, { payload: { id, comment } }) {
        return api.post(`/api/feedback/comment`, {
            feedbackSet: id,
            content: comment,
        });
    },
    deleteComment(_, { payload: { commentId } }) {
        return api.delete(`/api/feedback/comment/${commentId}`);
    },

    share(_, { payload: { id, sharedWith } }) {
        return api.put("/api/feedback/share", { id, sharedWith });
    },

    getListByUser: ({ commit }, { payload: { userId } }) => {
        return api.get(`/api/feedback/get/user/${userId}/sets/all`).then(res => {
            const data = res?.data ?? [];

            return commit("updateListByUser", { userId, data });
        });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = list;
    },
    updateDetails(state, { feedback }) {
        Vue.set(state.details, feedback._id, feedback);
    },
    updateListByUser(state, { userId, data }) {
        Vue.set(state.listByUser, userId, data);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
